// src/components/email/EmailGrupoList.jsx
// ═══════════════════════════════════════════════════════════════════════════
// 📧 LISTA DE GRUPOS DE EMAIL MARKETING
// ═══════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import Swal from 'sweetalert2';

const EmailGrupoList = () => {
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cargarGrupos();
  }, []);

  const cargarGrupos = async () => {
    try {
      setLoading(true);
      const res = await api.get('/email/grupos');
      setGrupos(res.data.grupos || []);
    } catch (error) {
      console.error('Error cargando grupos:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron cargar los grupos'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEliminar = async (id, nombre) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: '¿Eliminar Grupo?',
      html: `¿Estás seguro de eliminar el grupo <strong>${nombre}</strong>?<br><br><span style="color: #ef4444;">⚠️ Esta acción no se puede deshacer</span>`,
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`/email/grupos/${id}`);
      
      Swal.fire({
        icon: 'success',
        title: '✅ Eliminada',
        text: 'El grupo ha sido eliminado correctamente',
        timer: 2000,
        showConfirmButton: false
      });

      cargarGrupos();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.error || 'No se pudo eliminar el grupo'
      });
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Cargando grupos...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>👥 Grupos de Email Marketing</h1>
          <p style={styles.subtitle}>Segmenta tus contactos para campañas dirigidas</p>
        </div>
        <Link to="/email/grupos/nuevo" style={styles.btnPrimary}>
          <span>➕</span> Nuevo Grupo
        </Link>
      </div>

      {/* Grid de Grupos */}
      <div style={styles.gruposGrid}>
        {grupos.length > 0 ? (
          grupos.map(grupo => (
            <div key={grupo.id} style={styles.grupoCard}>
              <div style={styles.grupoHeader}>
                <div style={styles.grupoIcon}>📧</div>
                <div style={styles.grupoInfo}>
                  <h3 style={styles.grupoNombre}>{grupo.nombre}</h3>
                  <p style={styles.grupoDescripcion}>{grupo.descripcion || 'Sin descripción'}</p>
                </div>
              </div>

              <div style={styles.grupoStats}>
                <div style={styles.grupoStat}>
                  <span style={styles.statLabel}>Miembros</span>
                  <span style={styles.statValue}>{grupo.total_miembros || 0}</span>
                </div>
                <div style={styles.grupoStat}>
                  <span style={styles.statLabel}>Tipo</span>
                  <span style={styles.statValue}>{grupo.tipo || 'Manual'}</span>
                </div>
              </div>

              <div style={styles.grupoBadge}>
                {grupo.activo ? '✅ Activo' : '❌ Inactivo'}
              </div>

              <div style={styles.grupoActions}>
                <Link 
                  to={`/email/grupos/${grupo.id}/miembros`}
                  style={styles.btnEdit}
                >
                  ✏️ Miembros
                </Link>
                <button 
                  onClick={() => handleEliminar(grupo.id, grupo.nombre)}
                  style={styles.btnDelete}
                >
                  🗑️ Eliminar
                </button>
              </div>
            </div>
          ))
        ) : (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>📧</div>
            <h3 style={styles.emptyTitle}>No hay grupos creados</h3>
            <p style={styles.emptyText}>Crea tu primer grupo para segmentar contactos</p>
            <Link to="/email/grupos/nuevo" style={styles.btnPrimary}>
              Crear Primer Grupo
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// 🎨 ESTILOS
// ═══════════════════════════════════════════════════════════════════════════
const styles = {
  container: {
    minHeight: 'calc(100vh - 70px)',
    background: 'linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)',
    padding: '24px 32px',
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '16px',
    color: '#64748b'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 4px 0'
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0
  },
  btnPrimary: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: 'white',
    borderRadius: '8px',
    textDecoration: 'none',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
    transition: 'all 0.2s'
  },
  gruposGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: '20px'
  },
  grupoCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '2px solid #e2e8f0',
    transition: 'all 0.2s'
  },
  grupoHeader: {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px'
  },
  grupoIcon: {
    fontSize: '40px',
    background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
    padding: '12px',
    borderRadius: '12px'
  },
  grupoInfo: {
    flex: 1
  },
  grupoNombre: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 4px 0'
  },
  grupoDescripcion: {
    fontSize: '13px',
    color: '#64748b',
    margin: 0
  },
  grupoStats: {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px',
    padding: '12px',
    background: '#f8fafc',
    borderRadius: '8px'
  },
  grupoStat: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  statLabel: {
    fontSize: '11px',
    color: '#64748b',
    textTransform: 'uppercase',
    fontWeight: '600'
  },
  statValue: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1e293b'
  },
  grupoBadge: {
    padding: '6px 12px',
    background: '#d1fae5',
    color: '#065f46',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    display: 'inline-block',
    marginBottom: '16px'
  },
  grupoActions: {
    display: 'flex',
    gap: '10px'
  },
  btnEdit: {
    flex: 1,
    padding: '10px 16px',
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    color: 'white',
    borderRadius: '8px',
    border: 'none',
    fontWeight: '600',
    fontSize: '14px',
    textDecoration: 'none',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  btnDelete: {
    flex: 1,
    padding: '10px 16px',
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    color: 'white',
    borderRadius: '8px',
    border: 'none',
    fontWeight: '600',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  emptyState: {
    gridColumn: '1 / -1',
    textAlign: 'center',
    padding: '60px 20px',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '16px',
    opacity: 0.5
  },
  emptyTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '8px'
  },
  emptyText: {
    fontSize: '14px',
    color: '#64748b',
    marginBottom: '24px'
  }
};

export default EmailGrupoList;